import { useState, useEffect } from "react";
import i18n from "src/i18n";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    TbCheck,
    TbChevronLeft,
    TbBuildingHospital,
    TbNotes,
    TbCreditCard,
    TbPackage,
    TbUsers,
} from "react-icons/tb";
import ZaalaLogo from "src/components/ZaalaLogo/ZaalaLogo";
import LanguageSelect from "src/components/LanguageSelect/LanguageSelect";
import { APP_INFO } from "src/config/appInfo";
import { apiRequest } from "src/services/apiClient";
import { deactivateDemo } from "src/demo/demoApi";

import StepPraxis from "./steps/StepPraxis";
import StepAddress from "./steps/StepAddress";
import StepBanking from "./steps/StepBanking";
import StepPlan from "./steps/StepPlan";
import StepMembers from "./steps/StepMembers";

import { RegisterFormData, MemberData, PLAN_MAX_MEMBERS } from "./registerTypes";
import { getRegisterFieldErrors } from "./registerValidation";
import "./register.scss";

const STEPS = [
    { key: "register.step1", descKey: "register.step1Desc", Icon: TbBuildingHospital },
    { key: "register.step2", descKey: "register.step2Desc", Icon: TbNotes },
    { key: "register.step3", descKey: "register.step3Desc", Icon: TbCreditCard },
    { key: "register.step4", descKey: "register.step4Desc", Icon: TbPackage },
    { key: "register.step5", descKey: "register.step5Desc", Icon: TbUsers },
];

const INITIAL: RegisterFormData = {
    praxisName: "",
    praxisEmail: "",
    adminFirstName: "",
    adminLastName: "",
    praxisPassword: "",
    praxisConfirmPassword: "",
    praxisId: "",
    praxisIdManual: false,
    specialization: [],
    logo: "",
    phones: [],
    street: "",
    plz: "",
    city: "",
    country: "Deutschland",
    iban: "",
    bic: "",
    taxNumber: "",
    ibanConsent: false,
    plan: 1,
    members: [],
};

export default function Register() {
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        deactivateDemo();
    }, []);

    const [step, setStep] = useState(1);
    const [data, setData] = useState<RegisterFormData>(INITIAL);
    const [errors, setErrors] = useState<string[]>([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [registeredPraxisId, setRegisteredPraxisId] = useState("");
    const [agbAccepted, setAgbAccepted] = useState(false);
    const [datenschutzAccepted, setDatenschutzAccepted] = useState(false);
    const [avvAccepted, setAvvAccepted] = useState(false);
    // null = unchecked/checking, true = available, false = taken
    const [praxisIdAvailable, setPraxisIdAvailable] = useState<boolean | null>(null);

    // OTP verification
    const [showEmailInfo, setShowEmailInfo] = useState(false);
    const [otpPending,  setOtpPending]  = useState(false);
    const [otpSending,  setOtpSending]  = useState(false);
    const [otpValue,    setOtpValue]    = useState("");
    const [otpError,    setOtpError]    = useState<string | null>(null);

    const handlePlanChange = (plan: number) => {
        const max = PLAN_MAX_MEMBERS[plan] ?? 1;
        setData((d) => ({ ...d, plan, members: d.members.slice(0, max) }));
    };

    const validate = (): string[] =>
        getRegisterFieldErrors(step, data, { praxisIdAvailable, t });

    const handleNext = async () => {
        const errs = validate();
        if (errs.length) { setErrors(errs); return; }
        setErrors([]);

        // On step 1, check praxis email uniqueness before advancing
        if (step === 1) {
            try {
                const { available } = await apiRequest<{ available: boolean }>(
                    `/check-praxis-email?email=${encodeURIComponent(data.praxisEmail)}`,
                    { method: "GET" },
                );
                if (!available) {
                    setErrors([t("register.errorPraxisExists")]);
                    return;
                }
            } catch {
                // Non-blocking — if the check fails, let the user proceed
            }
        }

        // (Re-)populate the admin member when advancing from plan step to members step.
        // Always refresh from current praxis data so edits on earlier steps are reflected.
        if (step === 4) {
            setData(d => {
                const adminMember: MemberData = {
                    _key:            d.members[0]?._key ?? crypto.randomUUID(),
                    salutation:      d.members[0]?.salutation ?? "Herr",
                    title:           d.members[0]?.title ?? "",
                    firstName:       d.adminFirstName ?? "",
                    lastName:        d.adminLastName ?? "",
                    specialty:       d.specialization,
                    email:           d.praxisEmail,
                    password:        d.praxisPassword,
                    confirmPassword: d.praxisPassword,
                    phones:          d.phones.map(p => ({ ...p, id: crypto.randomUUID() })),
                    iban:            d.members[0]?.iban ?? "",
                    bic:             d.members[0]?.bic ?? "",
                    taxNumber:       d.members[0]?.taxNumber ?? "",
                    avatar:          d.members[0]?.avatar ?? "",
                    userRights:      d.members[0]?.userRights ?? 3,
                };
                return { ...d, members: [adminMember, ...d.members.slice(1)] };
            });
        }

        setStep((s) => s + 1);
        window.scrollTo(0, 0);
    };

    const handleBack = () => {
        setErrors([]);
        setStep((s) => s - 1);
        window.scrollTo(0, 0);
    };

    const handleSidebarStep = (n: number) => {
        if (n >= step) return; // only allow going back to completed steps
        setErrors([]);
        setStep(n);
        window.scrollTo(0, 0);
    };

    // Step 1: validate then show email info overlay
    const handleSubmit = () => {
        const errs = validate();
        if (!agbAccepted) errs.push("Bitte akzeptieren Sie die AGB.");
        if (!datenschutzAccepted) errs.push("Bitte stimmen Sie der Datenschutzerklärung zu.");
        if (!avvAccepted) errs.push("Bitte akzeptieren Sie den Auftragsverarbeitungsvertrag (AVV).");
        if (errs.length) { setErrors(errs); return; }
        setErrors([]);
        setShowEmailInfo(true);
    };

    // Step 2: send OTP (called after user confirms the email info overlay)
    const handleSendOtp = async () => {
        setShowEmailInfo(false);
        setOtpSending(true);
        setOtpValue("");
        setOtpError(null);
        try {
            await apiRequest(
                "/send-register-otp",
                { method: "POST", body: JSON.stringify({ email: data.praxisEmail }) },
                { "Content-Type": "application/json" },
            );
            setOtpPending(true);
        } catch (err: any) {
            if (err?.message === "TOO_MANY_ATTEMPTS") {
                setErrors([t("register.otpTooMany")]);
            } else {
                setErrors([t("register.otpSendError")]);
            }
        } finally {
            setOtpSending(false);
        }
    };

    // Step 2: send OTP + register payload together
    const handleRegister = async () => {
        const trimmedOtp = otpValue.trim();
        if (trimmedOtp.length !== 6) { setOtpError(t("register.otpInvalid")); return; }
        setSubmitting(true);
        setOtpError(null);
        try {
            const regResult = await apiRequest<{ praxisId: string }>(
                "/register",
                {
                    method: "POST",
                    body: JSON.stringify({
                        otp: trimmedOtp,
                        praxis: {
                            name: data.praxisName,
                            email: data.praxisEmail,
                            password: data.praxisPassword,
                            id: data.praxisId,
                            specialization: data.specialization,
                            logo: data.logo,
                            phones: data.phones.map((p) => p.value).filter(Boolean),
                            street: data.street,
                            plz: data.plz,
                            city: data.city,
                            country: data.country,
                            iban: data.iban,
                            bic: data.bic,
                            taxNumber: data.taxNumber,
                            plan: data.plan,
                        },
                        members: data.members.map((m, i) => ({
                            salutation: m.salutation,
                            title: m.title,
                            firstName: m.firstName,
                            lastName: m.lastName,
                            specialty: m.specialty,
                            email: m.email,
                            password: m.password,
                            phones: m.phones.map((p) => p.value).filter(Boolean),
                            iban: m.iban,
                            bic: m.bic,
                            taxNumber: m.taxNumber,
                            avatar: m.avatar,
                            userRights: i === 0 ? 3 : m.userRights,
                        })),
                    }),
                },
                { "Content-Type": "application/json" },
            );
            setRegisteredPraxisId(regResult.praxisId ?? "");
            setDone(true);
        } catch (err: any) {
            const msg = err?.message ?? "";
            if (msg === "OTP_INVALID" || msg === "OTP_REQUIRED") {
                setOtpError(t("register.otpInvalid"));
            } else if (msg === "IBAN_ALREADY_REGISTERED") {
                setOtpError(t("register.errorIbanRegistered"));
            } else {
                setOtpError(msg || t("register.errorGeneral"));
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (done) {
        return (
            <div className="register-page">
                <div className="register-success-card">
                    <img src="/medinvoice-logo.png" alt={APP_INFO.appName} className="register-brand-logo" />
                    <div className="register-success__icon"><TbCheck /></div>
                    <h2 className="register-success__title">{t("register.successTitle")}</h2>
                    <p className="register-success__msg">{t("register.successMsg")}</p>
                    {registeredPraxisId && (
                        <div className="register-success__praxis-id">
                            <span className="register-success__praxis-id-label">{t("register.yourPraxisId")}</span>
                            <code className="register-success__praxis-id-value">{registeredPraxisId}</code>
                        </div>
                    )}
                    <div className="register-success__verify-notice">
                        <span className="register-success__verify-notice-icon">📧</span>
                        <span>{t("register.verifyEmailNotice", { email: data.members[0]?.email || data.praxisEmail })}</span>
                    </div>
                    <button className="btn-primary" onClick={() => navigate("/login")}>
                        {t("register.goToLogin")}
                    </button>
                </div>
            </div>
        );
    }

    const currentStep = STEPS[step - 1];
    const StepIcon = currentStep.Icon;

    return (
        <div className="register-page">
            <div className="register-layout">

                {/* ── Left sidebar ── */}
                <aside className="register-sidebar">
                    <div className="register-sidebar__header">
                        <img src="/medinvoice-logo.png" alt={APP_INFO.appName} className="register-brand-logo--sidebar" />
                        <Link to="/login" className="register-sidebar__back-link">
                            <TbChevronLeft /> {t("register.backToLogin")}
                        </Link>
                    </div>

                    <h2 className="register-sidebar__title">{t("register.title")}</h2>

                    <nav className="register-sidebar__steps">
                        {STEPS.map(({ key, descKey, Icon }, i) => {
                            const n = i + 1;
                            const isDone   = step > n;
                            const isActive = step === n;
                            return (
                                <div
                                    key={key}
                                    className={
                                        "register-sidebar__step" +
                                        (isActive ? " register-sidebar__step--active" : "") +
                                        (isDone   ? " register-sidebar__step--done"   : "") +
                                        (isDone   ? " register-sidebar__step--clickable" : "")
                                    }
                                    onClick={isDone ? () => handleSidebarStep(n) : undefined}
                                >
                                    <div className="register-sidebar__step-icon-wrap">
                                        <div className="register-sidebar__step-icon">
                                            {isDone ? <TbCheck /> : <Icon />}
                                        </div>
                                        {i < STEPS.length - 1 && (
                                            <div className="register-sidebar__step-connector" />
                                        )}
                                    </div>
                                    <div className="register-sidebar__step-text">
                                        <span className="register-sidebar__step-name">{t(key)}</span>
                                        <span className="register-sidebar__step-desc">{t(descKey)}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </nav>
                </aside>

                {/* ── Right main panel ── */}
                <main className="register-main">
                    <div className="register-main__topbar">
                        {step > 1 ? (
                            <button type="button" className="register-back-btn" onClick={handleBack}>
                                <TbChevronLeft /> {t("register.back")}
                            </button>
                        ) : (
                            <div />
                        )}
                        <LanguageSelect />
                    </div>

                    <div className="register-main__body">
                        {/* Step header */}
                        <div className="register-main__step-header">
                            <div className="register-main__step-icon">
                                <StepIcon />
                            </div>
                            <div>
                                <h2 className="register-main__step-title">{t(currentStep.key)}</h2>
                                <p className="register-main__step-desc">{t(currentStep.descKey)}</p>
                            </div>
                        </div>

                        {/* Step content */}
                        <div className="register-step-content">
                            {step === 1 && <StepPraxis data={data} setData={setData} onIdStatus={v => setPraxisIdAvailable(v)} />}
                            {step === 2 && <StepAddress data={data} setData={setData} />}
                            {step === 3 && <StepBanking data={data} setData={setData} />}
                            {step === 4 && <StepPlan plan={data.plan} onChange={handlePlanChange} />}
                            {step === 5 && <StepMembers data={data} setData={setData} />}
                        </div>

                        {/* Legal checkboxes — shown on last step */}
                        {step === 5 && (
                            <div className="register-legal">
                                <label className="register-legal__item">
                                    <input
                                        type="checkbox"
                                        checked={agbAccepted}
                                        onChange={e => setAgbAccepted(e.target.checked)}
                                    />
                                    <span>Ich akzeptiere die <a href="/legal#agb" target="_blank" rel="noreferrer">AGB</a>.</span>
                                </label>
                                <label className="register-legal__item">
                                    <input
                                        type="checkbox"
                                        checked={datenschutzAccepted}
                                        onChange={e => setDatenschutzAccepted(e.target.checked)}
                                    />
                                    <span>Ich habe die <a href="/legal#datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</a> gelesen und stimme zu.</span>
                                </label>
                                <label className="register-legal__item">
                                    <input
                                        type="checkbox"
                                        checked={avvAccepted}
                                        onChange={e => setAvvAccepted(e.target.checked)}
                                    />
                                    <span>Ich akzeptiere den <a href="/legal#avv" target="_blank" rel="noreferrer">Auftragsverarbeitungsvertrag (AVV)</a> gemäß Art. 28 DSGVO.</span>
                                </label>
                            </div>
                        )}

                        {/* Navigation */}
                        <div className="register-nav">
                            {step < 5 ? (
                                <button type="button" className="btn-primary" onClick={handleNext}>
                                    {t("register.next")}
                                </button>
                            ) : (
                                <button type="button" className="btn-primary" onClick={handleSubmit} disabled={otpSending || submitting}>
                                    {otpSending ? t("register.submitting") : t("register.submit")}
                                </button>
                            )}
                        </div>

                        {/* Errors — fixed-height area so layout doesn't jump */}
                        <div className="register-errors-area">
                            {errors.length > 0 && (
                                <div className="register-errors">
                                    {errors.map((e, i) => (
                                        <div key={i} className="register-errors__item">{e}</div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </main>

                {/* ── Email info overlay ── */}
                {showEmailInfo && (
                    <div className="register-otp-overlay">
                        <div className="register-otp-box">
                            <h3 className="register-otp-box__title">{t("register.emailInfoTitle")}</h3>
                            <div className="register-email-info">
                                <div className="register-email-info__row">
                                    <span className="register-email-info__icon">📩</span>
                                    <div>
                                        <p className="register-email-info__label">{t("register.emailInfoPraxisLabel")}</p>
                                        <p className="register-email-info__value">{data.praxisEmail}</p>
                                        <p className="register-email-info__desc">{t("register.emailInfoPraxisDesc")}</p>
                                    </div>
                                </div>
                                <div className="register-email-info__row">
                                    <span className="register-email-info__icon">🔐</span>
                                    <div>
                                        <p className="register-email-info__label">{t("register.emailInfoAdminLabel")}</p>
                                        <p className="register-email-info__value">{data.members[0]?.email || "—"}</p>
                                        <p className="register-email-info__desc">{t("register.emailInfoAdminDesc")}</p>
                                    </div>
                                </div>
                            </div>
                            <div className="register-otp-box__actions">
                                <button
                                    type="button"
                                    className="register-back-btn"
                                    onClick={() => setShowEmailInfo(false)}
                                >
                                    {t("register.back")}
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleSendOtp}
                                    disabled={otpSending}
                                >
                                    {otpSending ? "…" : t("register.emailInfoConfirm")}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* ── OTP overlay ── */}
                {otpPending && (
                    <div className="register-otp-overlay">
                        <div className="register-otp-box">
                            <h3 className="register-otp-box__title">{t("register.otpTitle")}</h3>
                            <p className="register-otp-box__hint">
                                {t("register.otpHint", { email: data.praxisEmail })}
                            </p>
                            <input
                                className="register-otp-box__input"
                                type="text"
                                inputMode="numeric"
                                maxLength={6}
                                value={otpValue}
                                onChange={e => { setOtpValue(e.target.value.replace(/\D/g, "")); setOtpError(null); }}
                                onKeyDown={e => e.key === "Enter" && handleRegister()}
                                placeholder="000000"
                                autoFocus
                            />
                            {otpError && <p className="register-otp-box__error">{otpError}</p>}
                            <div className="register-otp-box__actions">
                                <button
                                    type="button"
                                    className="register-back-btn"
                                    onClick={() => { setOtpPending(false); setOtpError(null); }}
                                    disabled={submitting}
                                >
                                    {t("register.back")}
                                </button>
                                <button
                                    type="button"
                                    className="btn-primary"
                                    onClick={handleRegister}
                                    disabled={submitting || otpValue.length !== 6}
                                >
                                    {submitting ? t("register.submitting") : t("register.otpConfirm")}
                                </button>
                            </div>
                            <button
                                type="button"
                                className="register-otp-box__resend"
                                onClick={handleSendOtp}
                                disabled={otpSending || submitting}
                            >
                                {otpSending ? "…" : t("register.otpResend")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
